import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Skeleton, cn, type Column } from '@eombor/ui';
import type { IncomingDocRowDto } from '../api/accounting.api.js';

/**
 * PRIXOD HUJJATLARI — TELEFON VA PLANSHET KO'RINISHI.
 *
 * Jadval bilan BITTA ustunlar ta'rifidan quriladi (`columns`): sarlavha,
 * `render` va `mono` o'sha yerdan olinadi, ya'ni PDF tugmasi, 1C nishoni va
 * korxona nomi ikkala ko'rinishda ham aynan bitta kod bilan chiziladi.
 * Bu fayl faqat QAYERGA qo'yishni hal qiladi.
 *
 * Kartochkada maxsus o'rni yo'q ustun «yorliq: qiymat» setkasiga tushadi —
 * jadvalga yangi ustun qo'shilsa, u kartochkada o'zi paydo bo'ladi.
 */

/** Ustunning kartochkadagi o'rni — kalitlar `columns` dagi `key` lar. */
export interface IncomingDocCardLayout {
  /** Sarlavha — hujjat raqami. */
  title: string;
  /** O'ng yuqoridagi holat nishoni. */
  status: string;
  /** Ikkinchi qator — korxona. */
  subtitle: string;
  /** Pastdagi amallar. */
  actions: string;
}

export interface IncomingDocCardsProps {
  caption: string;
  columns: Column<IncomingDocRowDto>[];
  rows: IncomingDocRowDto[];
  layout: IncomingDocCardLayout;
  /** Jadvaldagi `onRowClick` bilan bir xil. */
  onOpen: (row: IncomingDocRowDto) => void;
}

/** Kirish animatsiyasi faqat shuncha kartochkada. */
const STAGGERED = 8;

const cellOf = (column: Column<IncomingDocRowDto>, row: IncomingDocRowDto): ReactNode => {
  if (column.render) return column.render(row);
  const value = (row as unknown as Record<string, unknown>)[column.key];
  if (value === null || value === undefined || value === '') return '—';
  return typeof value === 'string' || typeof value === 'number' ? value : String(value);
};

export const IncomingDocCards = ({
  caption,
  columns,
  rows,
  layout,
  onOpen,
}: IncomingDocCardsProps) => {
  const reduceMotion = useReducedMotion() === true;
  const byKey = (key: string) => columns.find((column) => column.key === key);
  const title = byKey(layout.title);
  const status = byKey(layout.status);
  const subtitle = byKey(layout.subtitle);
  const actions = byKey(layout.actions);
  const placed = new Set([layout.title, layout.status, layout.subtitle, layout.actions]);
  const meta = columns.filter((column) => !placed.has(column.key));

  return (
    <ul aria-label={caption} className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {rows.map((row, index) => {
        const animate = !reduceMotion && index < STAGGERED;
        return (
          <motion.li
            key={row.id}
            initial={animate ? { opacity: 0, y: 8 } : false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut', delay: animate ? index * 0.04 : 0 }}
            className={cn(
              /* `relative` — sarlavha tugmasining qoplamasi (`::after`)
                 butun kartochkani egallaydi: kartochka bosiladi, lekin
                 ichma-ich tugma hosil bo'lmaydi. */
              'relative flex h-full min-w-0 flex-col gap-3 rounded-2xl border border-border bg-surface p-4',
              'shadow-[var(--shadow-1)]',
              'transition-[background-color,border-color,transform] duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
              'hover:border-accent/30 has-[>div>button:active]:scale-[0.99] has-[>div>button:active]:bg-surface-2',
              'motion-reduce:has-[>div>button:active]:scale-100',
              'has-[>div>button:focus-visible]:outline-2 has-[>div>button:focus-visible]:outline-offset-2',
              'has-[>div>button:focus-visible]:outline-[var(--color-accent)]',
            )}
          >
            <div className="flex min-w-0 items-start justify-between gap-3">
              <button
                type="button"
                onClick={() => onOpen(row)}
                className={cn(
                  'min-w-0 truncate text-left font-mono text-base whitespace-nowrap focus-visible:shadow-none!',
                  "after:absolute after:inset-0 after:rounded-2xl after:content-['']",
                )}
              >
                {title === undefined ? row.id : cellOf(title, row)}
              </button>
              {status === undefined ? null : (
                <span className="relative z-[1] shrink-0">{cellOf(status, row)}</span>
              )}
            </div>

            {subtitle === undefined ? null : (
              <p className="line-clamp-2 text-sm font-medium text-text">{cellOf(subtitle, row)}</p>
            )}

            {meta.length === 0 ? null : (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl bg-surface-2 px-3 py-2.5">
                {meta.map((column) => (
                  <div key={column.key} className="flex min-w-0 flex-col gap-0.5">
                    <dt className="text-xs text-text-muted">{column.header}</dt>
                    <dd
                      className={cn(
                        'min-w-0 truncate text-sm text-text',
                        column.mono ? 'font-mono' : undefined,
                      )}
                    >
                      {cellOf(column, row)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}

            {actions === undefined ? null : (
              /* Amallar qoplama USTIDA (`z-[1]`) — PDF bosilganda hujjat
                 oynasi ochilmaydi. Tugmaning o'zi jadvaldagi bilan bir xil;
                 bu yerda faqat 44px balandlik va to'liq en beriladi. */
              <div className="relative z-[1] mt-auto flex border-t border-border pt-3 [&>button]:min-h-11 [&>button]:w-full">
                {cellOf(actions, row)}
              </div>
            )}
          </motion.li>
        );
      })}
    </ul>
  );
};

/** Yuklanish — kartochka shaklida, jadval skeletoni o'rniga. */
export const IncomingDocCardsSkeleton = ({ count = 4 }: { count?: number }) => (
  <ul aria-hidden className="grid grid-cols-1 gap-3 md:grid-cols-2">
    {Array.from({ length: count }, (_, i) => (
      <li
        key={i}
        className="flex flex-col gap-3 rounded-2xl border border-border bg-surface p-4 shadow-[var(--shadow-1)]"
      >
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-6 w-24" />
        </div>
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-11 w-full" />
      </li>
    ))}
  </ul>
);
